import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../api/axios";
import { RiH4 } from "react-icons/ri";

function CommunityCenterBooking() {

    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);
    const [saving, setSaving] = useState(false);

    const [center, setCenter] = useState(null);

    const [available, setAvailable] = useState(null);

    const [form, setForm] = useState({
        fromDate: "",
        toDate: "",

        applicantName: "",
        mobileNumber: "",
        email: "",
        address: "",

        eventType: "",
        purpose: "",

        numberOfPersons: "",

        facilitiesRequired: "",
        specialRequirements: "",

        idProofType: "",
        idProofNumber: "",

        remarks: ""
    });


    // ==========================================
    // LOAD CENTER
    // ==========================================

    useEffect(() => {

        loadCenter();

    }, [id]);


    const loadCenter = async () => {

        try {

            setLoading(true);

            const response =
                await api.get(
                    `/CommunityCenterBooking/GetCenter/${id}`
                );

            setCenter(response.data);

        } catch (error) {

            console.error(error);

            Swal.fire(
                "Error",
                "Community Center load nahi ho saka.",
                "error"
            );

        } finally {

            setLoading(false);
        }
    };


    // ==========================================
    // HANDLE CHANGE
    // ==========================================

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;

        setForm(prev => ({
            ...prev,
            [name]: value
        }));

        if (
            name === "fromDate" ||
            name === "toDate"
        ) {
            setAvailable(null);
        }
    };


    // ==========================================
    // DAYS
    // ==========================================

    const calculateDays = () => {

        if (
            !form.fromDate ||
            !form.toDate
        ) {
            return 0;
        }

        const from =
            new Date(form.fromDate);

        const to =
            new Date(form.toDate);

        const diff =
            Math.ceil(
                (to - from) /
                (1000 * 60 * 60 * 24)
            ) + 1;

        return diff > 0 ? diff : 0;
    };


    const numberOfDays =
        calculateDays();


    // ==========================================
    // AMOUNT
    // ==========================================

    const ratePerDay =
        Number(center?.ratePerDay) || 0;

    const cleaningCharges =
        Number(center?.cleaningCharges) || 0;

    const securityCharges =
        Number(center?.securityCharges) || 0;

    const gstPercentage =
        Number(center?.gstPercentage) || 18;


    const baseAmount =
        ratePerDay *
        numberOfDays;


    const subtotal =
        baseAmount +
        cleaningCharges +
        securityCharges;


    const gstAmount =
        Number(
            (
                subtotal *
                gstPercentage /
                100
            ).toFixed(2)
        );


    const totalAmount =
        subtotal +
        gstAmount;


    // ==========================================
    // CHECK AVAILABILITY
    // ==========================================

    const checkAvailability = async () => {

        if (!form.fromDate ||
            !form.toDate) {

            Swal.fire(
                "Required",
                "From Date और To Date select करें.",
                "warning"
            );

            return false;
        }


        if (
            form.fromDate >
            form.toDate
        ) {

            Swal.fire(
                "Invalid Date",
                "From Date To Date से बड़ी नहीं हो सकती.",
                "warning"
            );

            return false;
        }


        try {

            setChecking(true);

            const response =
                await api.get(
                    "/CommunityCenterBooking/CheckAvailability",
                    {
                        params: {
                            communityCenterId: id,
                            fromDate: form.fromDate,
                            toDate: form.toDate
                        }
                    }
                );


            setAvailable(
                response.data.available
            );


            if (response.data.available) {

                Swal.fire(
                    "Available",
                    "Community Center selected dates पर available है.",
                    "success"
                );

                return true;

            } else {

                Swal.fire(
                    "Not Available",
                    response.data.message,
                    "warning"
                );

                return false;
            }

        } catch (error) {

            console.error(error);

            Swal.fire(
                "Error",
                "Availability check नहीं हो सका.",
                "error"
            );

            return false;

        } finally {

            setChecking(false);
        }
    };


    // ==========================================
    // SAVE
    // ==========================================

    const handleSubmit = async (e) => {

        e.preventDefault();


        if (!form.applicantName.trim()) {

            Swal.fire(
                "Required",
                "Applicant Name required है.",
                "warning"
            );

            return;
        }


        if (!form.mobileNumber.trim()) {

            Swal.fire(
                "Required",
                "Mobile Number required है.",
                "warning"
            );

            return;
        }


        if (
            form.mobileNumber.length !== 10
        ) {

            Swal.fire(
                "Invalid Mobile",
                "10 digit mobile number डालें.",
                "warning"
            );

            return;
        }


        if (
            !form.fromDate ||
            !form.toDate
        ) {

            Swal.fire(
                "Required",
                "Booking dates select करें.",
                "warning"
            );

            return;
        }


        if (numberOfDays <= 0) {

            Swal.fire(
                "Invalid Date",
                "Invalid booking dates.",
                "warning"
            );

            return;
        }


        // Always check again before save

        const isAvailable =
            await checkAvailability();


        if (!isAvailable) {
            return;
        }


        try {

            setSaving(true);


            const userId =
                localStorage.getItem("userId");


            const payload = {

                communityCenterId:
                    Number(id),

                fromDate:
                    form.fromDate,

                toDate:
                    form.toDate,

                applicantName:
                    form.applicantName,

                mobileNumber:
                    form.mobileNumber,

                email:
                    form.email || null,

                address:
                    form.address || null,

                eventType:
                    form.eventType || null,

                purpose:
                    form.purpose || null,

                numberOfPersons:
                    form.numberOfPersons
                        ? Number(form.numberOfPersons)
                        : null,

                facilitiesRequired:
                    form.facilitiesRequired || null,

                specialRequirements:
                    form.specialRequirements || null,

                idProofType:
                    form.idProofType || null,

                idProofNumber:
                    form.idProofNumber || null,

                remarks:
                    form.remarks || null,

                createdBy:
                    userId
                        ? Number(userId)
                        : null
            };


            const response =
                await api.post(
                    "/CommunityCenterBooking/Save",
                    payload
                );


            if (response.data.success) {

                Swal.fire({
                    icon: "success",
                    title: "Booking Submitted",
                    html: `
                        <div>
                            <p>
                                Booking ID:
                                <strong>
                                    ${response.data.bookingId}
                                </strong>
                            </p>

                            <p>
                                Total Amount:
                                <strong>
                                    ₹${Number(
                                        response.data.totalAmount
                                    ).toFixed(2)}
                                </strong>
                            </p>
                        </div>
                    `
                });


                navigate(
                    `/CommunityCenterBookingDetails/${response.data.bookingId}`
                );
            }

        } catch (error) {

            console.error(error);

            Swal.fire(
                "Error",
                error.response?.data?.message ||
                "Booking save नहीं हो सकी.",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    // ==========================================
    // LOADING
    // ==========================================

    if (loading) {

        return (
            <div className="text-center p-5">

                <div
                    className="spinner-border text-primary"
                />

                <p className="mt-2">
                    Loading...
                </p>

            </div>
        );
    }


    if (!center) {
        return null;
    }


    return (

       

            <div className="card shadow-sm">

               <div class="card-header bg-primary text-white">

                    <h4 className="mb-0">
                        Community Center Booking Form
                    </h4>

                </div>


                <div className="card-body">
 <div className="row">

                    {/* ================================= */}
                    {/* CENTER DETAILS alert */}
                    {/* ================================= */}

                   <div className="col-md-5">
     {/* ================================= */}
                            {/* AMOUNT */}
                            {/* ================================= */}

                            

                                <div className="card bg-light">

                                    <div className="card-body">
<h4>
                                    {center.communityCenterName}
                                </h4>

                                <p>
                                    <strong>
                                        CC Location : 
                                    </strong>{" "}
                                    {center.communityCenterLocation}
                                </p>
                                        
                                           
                                       

                                        <div className="row">

                                            <div className="col-md-12">
{/* <p> Booking Charges</p> */}
                                                <table className="table table-sm">

                                                    <tbody>

                                                        <tr>
                                                            <td>
                                                                Rate Per Day
                                                            </td>
                                                            <td className="text-end">
                                                                ₹{ratePerDay.toFixed(2)}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td>
                                                                Number of Days
                                                            </td>
                                                            <td className="text-end">
                                                                {numberOfDays}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td>
                                                                Base Amount
                                                            </td>
                                                            <td className="text-end">
                                                                ₹{baseAmount.toFixed(2)}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td>
                                                                Cleaning Charges
                                                            </td>
                                                            <td className="text-end">
                                                                ₹{cleaningCharges.toFixed(2)}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td>
                                                                Security Charges
                                                            </td>
                                                            <td className="text-end">
                                                                ₹{securityCharges.toFixed(2)}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td>
                                                                GST ({gstPercentage}%)
                                                            </td>
                                                            <td className="text-end">
                                                                ₹{gstAmount.toFixed(2)}
                                                            </td>
                                                        </tr>

                                                        <tr className="fw-bold">

                                                            <td>
                                                                Total Amount
                                                            </td>

                                                            <td className="text-end">
                                                                ₹{totalAmount.toFixed(2)}
                                                            </td>

                                                        </tr>

                                                    </tbody>

                                                </table>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                           
</div>
 <div className="col-md-7">
                    <div className="alert-light border">

                        <div className="">

                            {/* <div className="col-md-8">

                                <h4>
                                    {center.communityCenterName}
                                </h4>

                                <p className="mb-1">
                                    <strong>
                                        CC Location : 
                                    </strong>{" "}
                                    {center.communityCenterLocation}
                                </p>

                                <p className="mb-1">
                                    <strong>
                                        CC Rent Price : 
                                    </strong>{" "}
                                    ₹{ratePerDay.toFixed(2)} Per Day
                                </p>

                                <p className="mb-1">
                                    <strong>
                                        Cleaning Charge : 
                                    </strong>{" "}
                                    ₹{cleaningCharges.toFixed(2)} Per Day
                                </p>

                                <p className="mb-0">
                                    <strong>
                                        Security Charge : 
                                    </strong>{" "}
                                    ₹{securityCharges.toFixed(2)}
                                </p>

                            </div> */}


                            {/* <div className="col-md-4 text-md-end"> */}

                                {center.ccImage && (

                                    <img
                                        src={
                                            center.ccImage.startsWith("http")
                                                ? center.ccImage
                                                : `${api.defaults.baseURL?.replace("/api", "")}${center.ccImage}`
                                        }
                                        alt={
                                            center.communityCenterName
                                        }

                                        className="img-fluid rounded"
                                        // style={{
                                        //     width: "150px",
                                        //     height: "100px",
                                        //     objectFit: "cover",
                                        //     borderRadius: "8px"
                                        // }}
                                    />

                                )}

                            {/* </div> */}

                        </div>

                    </div>
                   </div>
 <div className="col-md-12 p-4">
                    <form onSubmit={handleSubmit}>

                        <div className="row">


                            {/* ================================= */}
                            {/* DATES */}
                            {/* ================================= */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    From Date
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="date"
                                    name="fromDate"
                                    value={form.fromDate}
                                    onChange={handleChange}
                                    min={
                                        new Date()
                                            .toISOString()
                                            .split("T")[0]
                                    }
                                    className="form-control"
                                />

                            </div>


                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    To Date
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="date"
                                    name="toDate"
                                    value={form.toDate}
                                    onChange={handleChange}
                                    min={
                                        form.fromDate ||
                                        new Date()
                                            .toISOString()
                                            .split("T")[0]
                                    }
                                    className="form-control"
                                />

                            </div>


                            {/* ================================= */}
                            {/* AVAILABILITY */}
                            {/* ================================= */}

                           <div className="col-md-4 mb-3 text-center">

                                <label className="form-label">
                                   &nbsp;.
                                </label>

                                


                                {available === true && (

                                    <span className="text-success ms-3">
                                        <i className="bi bi-check-circle"></i>{" "}
                                        Available &nbsp;
                                    </span>

                                )}


                                {available === false && (

                                    <span className="text-danger ms-3">
                                        <i className="bi bi-x-circle"></i>{" "}
                                        Not Available &nbsp;
                                    </span>

                                )}

                               <br></br>
<button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={
                                        checkAvailability
                                    }
                                    disabled={checking}
                                >

                                    {checking
                                        ? "Checking..."
                                        : "Check Availability"
                                    }

                                </button>
                            </div>


{/* ================================= */}
                            {/* EVENT */}
                            {/* ================================= */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Event Type
                                </label>

                                <select
                                    name="eventType"
                                    value={form.eventType}
                                    onChange={handleChange}
                                    className="form-select"
                                >

                                    <option value="">
                                        Select Event Type
                                    </option>

                                    <option value="Marriage">
                                        Marriage
                                    </option>

                                    <option value="Birthday">
                                        Birthday
                                    </option>

                                    <option value="Meeting">
                                        Meeting
                                    </option>

                                    <option value="Function">
                                        Function
                                    </option>

                                    <option value="Other">
                                        Other
                                    </option>

                                </select>

                            </div>


                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Purpose
                                </label>

                                <input
                                    type="text"
                                    name="purpose"
                                    value={form.purpose}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>

                              <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Number of Persons
                                </label>

                                <input
                                    type="number"
                                    min="1"
                                    name="numberOfPersons"
                                    value={
                                        form.numberOfPersons
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>

                            {/* ================================= */}
                            {/* APPLICANT */}
                            {/* ================================= */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Applicant Name *
                                </label>

                                <input
                                    type="text"
                                    name="applicantName"
                                    value={
                                        form.applicantName
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Mobile Number *
                                </label>

                                <input
                                    type="text"
                                    name="mobileNumber"
                                    value={
                                        form.mobileNumber
                                    }
                                    onChange={(e) => {

                                        const value =
                                            e.target.value.replace(
                                                /\D/g,
                                                ""
                                            );

                                        if (
                                            value.length <= 10
                                        ) {

                                            setForm(prev => ({
                                                ...prev,
                                                mobileNumber:
                                                    value
                                            }));
                                        }

                                    }}
                                    className="form-control"
                                    maxLength="10"
                                />

                            </div>


                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Email
                                </label>

                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                          

                            {/* ================================= */}
                            {/* ADDRESS */}
                            {/* ================================= */}

                            <div className="col-md-12 mb-3">

                                <label className="form-label">
                                    Address
                                </label>

                                <textarea
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    className="form-control"
                                    rows="2"
                                />

                            </div>


                            


                            {/* ================================= */}
                            {/* ID PROOF */}
                            {/* ================================= */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    ID Proof Type
                                </label>

                                <select
                                    name="idProofType"
                                    value={form.idProofType}
                                    onChange={handleChange}
                                    className="form-select"
                                >

                                    <option value="">
                                        Select
                                    </option>

                                    <option value="Aadhaar">
                                        Aadhaar
                                    </option>

                                    <option value="PAN">
                                        PAN
                                    </option>

                                    <option value="Driving License">
                                        Driving License
                                    </option>

                                    <option value="Voter ID">
                                        Voter ID
                                    </option>

                                </select>

                            </div>


                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    ID Proof Number
                                </label>

                                <input
                                    type="text"
                                    name="idProofNumber"
                                    value={
                                        form.idProofNumber
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>
                             <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    ID Proof Doc *  ('.JPG/PDF')
                                </label>

                                <input type="file" name=""  className="form-control"></input>
                                </div>


                           


                            {/* ================================= */}
                            {/* FACILITIES */}
                            {/* ================================= */}

                            <div className="col-md-12 mb-3">

                                <label className="form-label">
                                    Facilities Required
                                </label>

                                <input
                                    type="text"
                                    name="facilitiesRequired"
                                    value={
                                        form.facilitiesRequired
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="e.g. Chairs, Tables, AC..."
                                />

                            </div>


                            <div className="col-md-12 mb-3">

                                <label className="form-label">
                                    Special Requirements
                                </label>

                                <textarea
                                    name="specialRequirements"
                                    value={
                                        form.specialRequirements
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                    rows="3"
                                />

                            </div>


                            <div className="col-md-12 mb-3">

                                <label className="form-label">
                                    Remarks
                                </label>

                                <textarea
                                    name="remarks"
                                    value={form.remarks}
                                    onChange={handleChange}
                                    className="form-control"
                                    rows="2"
                                />

                            </div>


                        <div className="col-md-4"  style={{visibility:'hidden',height:'2px'}}>

                                <label className="form-label">
                                    GST
                                </label>

                                <input
                                    type="text"
                                    value={`${gstPercentage}%`}
                                    readOnly
                                    className="form-control" 
                                />

                            </div>

                        </div>


                        {/* ================================= */}
                        {/* SUBMIT */}
                        {/* ================================= */}

                        <div className="mt-4 d-flex gap-2">

                            <button
                                type="submit"
                                className="btn btn-success"
                                disabled={saving}
                            >

                                {saving
                                    ? "Submitting..."
                                    : "Submit Booking"
                                }

                            </button>


                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() =>
                                    navigate(
                                        "/CommunityCenterList"
                                    )
                                }
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

</div>
  <div className="col-md-4"></div>


</div>
                </div>

            </div>

      
    );
}

export default CommunityCenterBooking;